// |--------------------------------------------------------------------------
// | DATA ANALYSER
// |--------------------------------------------------------------------------
// | Main script for data analysis, processing and manipulation
// |

import axios from '@/axios'
import lodashGet from 'lodash.get'

import { createHttpQuery, articleHeader } from '@/helpers'
import * as settings from '@/analytics/settings/data-analyser-settings'
import * as utils from '@/analytics/utils/data-analyser-utils'
import { getTimeframe } from '@/analytics/utils/keen-utils'
import keen from '@/analytics/keen'

async function getItems(type, params) {
  if (type === 'article') {
    params = { ...params, populate: ['allTags'] }
  }

  if (type === 'partner') {
    params.channel_ids = params.partner_ids
    delete params.partner_ids
    type = 'channel'
  }

  params = { ...params, per_page: 50 }

  params = createHttpQuery(params)

  type =
    type === 'company'
      ? 'companies'
      : type === 'article'
      ? type + 's/ids'
      : type + 's'

  const resp = await axios.get(`/${type}`, {
    params,
  })

  const result = {}

  for (const item of resp.data.data) {
    result[item.id] = item
  }
  return result
}

export async function getArticles(params) {
  params = createHttpQuery(params)

  const resp = await axios.get(`/articles`, {
    params,
  })

  return resp.data.data
}

export function constructBasicMetrics(
  data,
  props,
  sources,
  timeChunks,
  type = null
) {
  const result = {}

  for (const timeChunk of timeChunks) {
    result[timeChunk] = calcBasicMetrics(data, props, sources, timeChunk, type)
  }

  return result
}

function calcBasicMetrics(data, props, sources, timeChunk, type) {
  // console.log('calcBasicMetrics', data, props)
  const result = {
    ...(props.includes('emailViews') && {
      emailViews: utils.sumCountEvents(
        data['sendgrid']['email_open']?.[timeChunk]
      ),
    }),
    ...(props.includes('emailViewsChannel') && {
      emailViewsChannel: utils.calcAvgType(
        data['channel']['email']['email_article_views']?.[timeChunk]
      ),
    }),
    ...(props.includes('emailViewsNetwork') && {
      emailViewsNetwork: utils.calcAvgType(
        data['network']['email']['email_article_views']?.[timeChunk]
      ),
    }),
    ...(props.includes('emailViewsUnique') && {
      emailViewsUnique: utils.sumCountEventsUniqueUser(
        data['sendgrid']['email_article_views']?.[timeChunk]
      ),
    }),
    //
    ...(props.includes('emailReads') && {
      emailReads: utils.sumCountEventsGrouped(
        data['emailReadCategory']['email_article_views']?.[timeChunk],
        'read_category',
        'read'
      ),
    }),
    // ...(props.includes('emailReads') && {
    //   emailReads: utils.sumCountEvents(
    //     data['sendgrid']['email_click']?.[timeChunk]
    //   )
    // }),

    ...(props.includes('emailReadsChannel') && {
      emailReadsChannel: utils.calcAvgType(
        data['channel']['emailReadCategory']['email_article_views']?.[timeChunk],
        'article',
        'read_category',
        'read'
      ),
    }),
    ...(props.includes('emailReadsNetwork') && {
      emailReadsNetwork: utils.calcAvgType(
        data['network']['emailReadCategory']['email_article_views']?.[timeChunk],
        'article',
        'read_category',
        'read'
      ),
    }),
    ...(props.includes('emailReadsUnique') && {
      emailReadsUnique: utils.sumCountEventsGrouped(
        data['emailReadCategory']['email_article_views']?.[timeChunk],
        'read_category',
        'read',
        'count_events_unique_user'
      ),
    }),

    ...(props.includes('emailNewsletterViews') && {
      emailNewsletterViews: utils.sumCountEvents(
        data['sendgrid']['email_open']?.[timeChunk]
      ),
    }),
    ...(props.includes('emailNewsletterViewsNetwork') &&
      {
        // emailNewsletterViewsNetwork: utils.calcAvgType(
        //   data['network']['sendgrid']['email_newsletter_views']?.[timeChunk],
        //   type
        // )
      }),
    ...(props.includes('emailNewsletterViewsUnique') &&
      {
        // emailNewsletterViewsUnique: utils.sumCountEventsUniqueUser(
        //   data['sendgrid']['email_newsletter_views']?.[timeChunk]
        // )
      }),

    ...(props.includes('emailNewsletterReads') &&
      {
        // emailNewsletterReads: utils.sumCountEventsGrouped(
        //   data['emailNewsletterReadCategory']['email_newsletter_views'][
        //     timeChunk
        //   ],
        //   'read_category',
        //   'read'
        // )
      }),
    ...(props.includes('emailNewsletterReadsNetwork') &&
      {
        // emailNewsletterReadsNetwork: utils.calcAvgType(
        //   data['network']['emailNewsletterReadCategory'][
        //     'email_newsletter_views'
        //   ]?.[timeChunk],
        //   type,
        //   'read_category',
        //   'read'
        // )
      }),
    ...(props.includes('emailNewsletterReadsUnique') && {
      emailNewsletterReadsUnique: utils.sumCountEvents(
        data['sendgrid']['email_open']?.[timeChunk]
      ),
    }),

    ...(props.includes('webViews') && {
      webViews: utils.sumCountEvents(data['web']['article_views']?.[timeChunk]),
    }),
    ...(props.includes('webViewsChannel') && {
      webViewsChannel: utils.calcAvgType(
        data['channel']['web']['article_views']?.[timeChunk]
      ),
    }),
    ...(props.includes('webViewsNetwork') && {
      webViewsNetwork: utils.calcAvgType(
        data['network']['web']['article_views']?.[timeChunk]
      ),
    }),
    ...(props.includes('webViewsUnique') && {
      webViewsUnique: utils.sumCountEventsUniqueUser(
        data['web']['article_views']?.[timeChunk]
      ),
    }),

    ...(props.includes('webDownloads') && {
      webDownloads: utils.sumCountEventsUniqueUser(
        data['web']['article_downloads']?.[timeChunk]
      ),
    }),
    ...(props.includes('webDownloadsChannel') && {
      webDownloadsChannel: utils.calcAvgType(
        data['channel']['web']['article_downloads']?.[timeChunk]
      ),
    }),
    ...(props.includes('webDownloadsNetwork') && {
      webDownloadsNetwork: utils.calcAvgType(
        data['network']['web']['article_downloads']?.[timeChunk]
      ),
    }),

    ...(props.includes('scrollDepth') && {
      scrollDepth: utils.calcAvgPerc(data['web']['article_views']?.[timeChunk]),
    }),
    ...(props.includes('engagedMinutes') && {
      engagedMinutes: utils.sumCountEventsUniqueUser(
        data['web']['article_views']?.[timeChunk]
      ),
    }),
    ...(props.includes('engagedMinutesNewsletter') && {
      engagedMinutesNewsletter: utils.calcEngagement(
        [data['sendgrid']['email_click']],
        timeChunk
      ),
    }),
    ...(props.includes('avgReadTime') && {
      avgReadTime: utils.calcAvgReadTime(
        data['sendgrid']['email_article_views'],
        data['web']['article_views'],
        timeChunk
      ),
    }),

    ...(props.includes('sendgridProcessed') && {
      sendgridProcessed: utils.sumCountEvents(
        data['sendgrid']['email_processed']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridDelivered') && {
      sendgridDelivered: utils.sumCountEvents(
        data['sendgrid']['email_delivered']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridOpened') && {
      sendgridOpened: utils.sumCountEvents(
        data['sendgrid']['email_open']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridClicked') && {
      sendgridClicked: utils.sumCountEvents(
        data['sendgrid']['email_click']?.[timeChunk]
      ),
    }),

    ...(props.includes('sendgridProcessedUnique') && {
      sendgridProcessedUnique: utils.sumCountEventsUniqueUser(
        data['sendgrid']['email_processed']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridDeliveredUnique') && {
      sendgridDeliveredUnique: utils.sumCountEventsUniqueUser(
        data['sendgrid']['email_delivered']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridOpenedUnique') && {
      sendgridOpenedUnique: utils.sumCountEventsUniqueUser(
        data['sendgrid']['email_open']?.[timeChunk]
      ),
    }),
    ...(props.includes('sendgridClickedUnique') && {
      sendgridClickedUnique: utils.sumCountEventsUniqueUser(
        data['sendgrid']['email_click']?.[timeChunk]
      ),
    }),

    ...(props.includes('sendgridOpenRate') && {
      sendgridOpenRate:
        utils.sumCountEventsUniqueUser(
          data['sendgrid']['email_delivered']?.[timeChunk]
        ) === 0
          ? 0
          : Math.round(
              (utils.sumCountEventsUniqueUser(
                data['sendgrid']['email_open']?.[timeChunk]
              ) /
                utils.sumCountEventsUniqueUser(
                  data['sendgrid']['email_delivered']?.[timeChunk]
                )) *
                100
            ),
    }),

    ...(props.includes('sendgridProcessedNetwork') && {
      sendgridProcessedNetwork: utils.calcAvgType(
        data['networkNewsletterType']['sendgrid']['email_processed']?.[timeChunk],
        type
      ),
    }),
    ...(props.includes('sendgridDeliveredNetwork') && {
      sendgridDeliveredNetwork: utils.calcAvgType(
        data['networkNewsletterType']['sendgrid']['email_delivered']?.[timeChunk],
        type
      ),
    }),
    ...(props.includes('sendgridOpenedNetwork') && {
      sendgridOpenedNetwork: utils.calcAvgType(
        data['networkNewsletterType']['sendgrid']['email_open']?.[timeChunk],
        type
      ),
    }),
    ...(props.includes('sendgridClickedNetwork') && {
      sendgridClickedNetwork: utils.calcAvgType(
        data['networkNewsletterType']['sendgrid']['email_click']?.[timeChunk],
        type
      ),
    }),
  }

  // console.log(result)
  return result
}

export function constructFunnelItems(metrics, type) {
  const funnel = {}
  let items = []

  if (type === 'article') {
    items = [
      // metrics.sendgridDeliveredUnique,
      metrics.emailDelivers || 0,
      // metrics.sendgridOpenedUnique,
      // metrics.emailClicks || 0,
      metrics.emailOpens || 0,
      metrics.webViews || 0,
      metrics.downloads || 0,
    ]
  } else if (type === 'newsletter') {
    items = [
      metrics.sendgridDeliveredUnique || 0,
      metrics.sendgridOpenedUnique || 0,
      // metrics.emailNewsletterReadsUnique,
      metrics.sendgridClickedUnique || 0,
      metrics.sendgridClickedUnique || 0,
    ]
  }

  funnel[type] = items
  // funnel['channel'] = [metrics.sendgridDeliveredChannel, metrics.sendgridOpenedChannel, metrics.emailViewsUniqueChannel, metrics.webViewsUniqueChannel, metrics.downloadsChannel, metrics.downloadsChannel]
  // network: [5028, 2289, 1886, 380, 152, 152]
  return funnel
}

export function constructReadership(data, sources, type, avgs = []) {
  let result = {}

  // console.log('askdjfkjsadfkj',data, sources, type)
  for (const source in sources) {
    if (sources[source] !== undefined) {
      result = {
        ...result,
        ...getSourceReadership(data, source, sources[source], type, avgs),
      }
    }
  }

  return result
}

export function constructReadershipGrouped(data, sources, type, avgs = []) {
  const result = []

  for (const source in sources) {
    // console.log('hi there', sources[source])
    if (sources[source] !== undefined) {
      const val = getSourceReadership(
        data,
        source,
        sources[source],
        type,
        avgs,
        true
      )

      if (val) {
        // console.log('THIS IS IT?', source)
        const sourceName = settings.readershipSourceKeyMap[source]

        result.push({
          key: sourceName,
          name:
            sourceName in settings.readershipKeyNameMap
              ? settings.readershipKeyNameMap[sourceName]
              : sourceName,
          val: val,
        })
      }
    }
  }

  return result
}

export function getSourceReadership(
  data,
  source,
  sourceObj,
  type,
  avgs,
  grouped = false
) {
  const result = {}

  const stream = sourceObj[0]
  // console.log('HHSDUKJFhjsdkfakjhsadfkhjdsakfj',stream, source, sourceObj, data)

  if (source === 'emailReadCategory') {
    result[grouped ? type : 'emailReads'] = utils.getCountEvents(
      data[source][stream]['current'],
      'count_events',
      'read_category',
      'read'
    )
  } else {
    result[grouped ? type : source] = utils.getCountEvents(
      data[source][stream]['current']
    )
  }

  for (const avg of avgs) {
    if (source === 'emailReadCategory') {
      result[grouped ? avg : 'emailReads' + utils.capitalise(avg)] = data[avg][
        source
      ][stream]['current'].map((event) =>
        utils.calcAvgSingle(event, type, 'read_category', 'read')
      )
    } else {
    // console.log('looking for', type, 'in', result)
      // console.log('source', source, data, avg, source, grouped ? avg : source + utils.capitalise(avg))
      result[grouped ? avg : source + utils.capitalise(avg)] = data[avg]?.[
        source
      ]?.[stream]?.['current']?.map((event) => utils.calcAvgSingle(event, type)) || []
    }
  }

  return result
}

export function constructBarChartItems(metrics, props, type) {
  const result = []

  for (const prop of props) {
    const val = {
      [type]: metrics[prop],
    }

    if (metrics[`${prop}Channel`] != undefined) {
      val.channel =
        metrics[`${prop}Channel`] && metrics[`${prop}Channel`] > -1
          ? metrics[`${prop}Channel`]
          : 0
    }

    val.network =
      metrics[`${prop}Network`] && metrics[`${prop}Network`] > -1
        ? metrics[`${prop}Network`]
        : 0

    result.push({
      key: prop,
      name:
        prop in settings.barChartKeyNameMap
          ? settings.barChartKeyNameMap[prop]
          : prop,
      val,
    })
  }

  return result
}

export async function constructTopList(
  indexBy,
  groupBy,
  attr,
  sources,
  timeChunks,
  timeframeMode,
  id,
  context,
  resourceId,
  topRanks,
  basicMetrics
) {
  const timeframe = getTimeframe(timeframeMode)
  const idsArr = utils.setupPermissionIds(indexBy, groupBy, id)
  const fetchType =
    timeframe.includes('years') || timeframeMode === 'ytd' ? 'single' : 'double'
  const analyses = await keen.fetchAnalyses(
    groupBy,
    sources,
    timeframe,
    idsArr,
    fetchType
  )

  const engagement = calcEngagementGrouped(analyses, sources, timeChunks, attr)

  // Add current resource engagement to engagement list if not present and type equals resource type
  if (
    context === 'rank' &&
    ((resourceId !== null && attr.includes(groupBy)) ||
      (groupBy === 'article' && attr.includes('alertable'))) &&
    basicMetrics !== null &&
    basicMetrics !== undefined &&
    basicMetrics.current !== undefined &&
    basicMetrics.current.engagedMinutes !== undefined
  ) {
    if (!(resourceId in engagement.current)) {
      engagement.current[resourceId] = basicMetrics.current.engagedMinutes
    }
    if (
      engagement.previous !== undefined &&
      !(resourceId in engagement.previous)
    ) {
      engagement.previous[resourceId] =
        basicMetrics.previous !== undefined &&
        basicMetrics.previous.engagedMinutes !== undefined
          ? basicMetrics.previous.engagedMinutes
          : 0
    }
    const addons = ['webDownloads']

    for (const addon of addons) {
      if (
        basicMetrics.current[addon] !== undefined &&
        basicMetrics.current[addon] > -1
      ) {
        engagement[addon] = { [resourceId]: basicMetrics.current[addon] }
      }
    }
  }

  // if ('web' in sources && sources.web.includes('article_views') && groupBy === 'account') {
    // console.log(analyses)
  //   addAttribute(
  //     engagement,
  //     analyses.web.article_views,
  //     'sum_read_seconds',
  //     'current'
  //   )
  // }

  // if ('web' in sources && sources.web.includes('article_downloads')) {
  //   addAttribute(
  //     engagement,
  //     analyses.web.article_downloads,
  //     'count_events_unique_user',
  //     'webDownloads'
  //   )
  // }

  // if ('email' in sources && sources.email.includes('email_article_views')) {
  //   addAttribute(
  //     engagement,
  //     analyses.email.email_article_views,
  //     'count_events',
  //     'emailViews'
  //   )
  // }

  // if (
  //   'emailNewsletter' in sources &&
  //   sources.emailNewsletter.includes('email_newsletter_views')
  // ) {
  //   addAttribute(
  //     engagement,
  //     analyses.sendgrid.email_open,
  //     'count_events',
  //     'emailViews'
  //   )
  // }

  // if ('sendgrid' in sources && sources.sendgrid.includes('email_open')) {
  //   addAttribute(
  //     engagement,
  //     analyses.sendgrid.email_open,
  //     'count_events',
  //     'sendgridOpened'
  //   )
  // }


  const processed = processDataGrouped(
    engagement,
    groupBy,
    context,
    resourceId,
    topRanks,
    timeframeMode
  )
  // console.log({processed})
  const enriched = await enrichDataGrouped(
    processed,
    indexBy,
    groupBy,
    id,
    context
  )
  return enriched
}

export function deriveTopList(items, type, topRanks = 20, context, timeframe) {
  let results = items.slice()

  const itemsObj = {}
  for (const item of results) {
    if (type === 'topic' && 'tags' in item) {
      for (const tag of item.tags) {
        setSubObject(item, tag, itemsObj, type)
      }
    } else if (type === 'geography' && 'place' in item) {
      setSubObject(item, item.place, itemsObj, type)
    }
  }

  results = []
  for (const key in itemsObj) {
    if (itemsObj[key] !== undefined) {
      const subObj = itemsObj[key]
      const diffNum = utils.percDiff(
        subObj.value,
        subObj.lastValue,
        false,
        timeframe
      )
      const diffStr = utils.percDiff(
        subObj.value,
        subObj.lastValue,
        true,
        timeframe
      )

      results.push({
        diffNum,
        diffStr,
        id: subObj.el.id,
        name: subObj.el.name,
        value: subObj.value,
        lastValue: subObj.lastValue,
      })
    }
  }
  utils.sortDesc(results)
  results = enrichTopListRank(results, null, topRanks, context)

  return results
}

function setSubObject(item, subObj, obj, type) {
  if (!settings.bubblecloudBlacklists[type].includes(parseInt(subObj.id))) {
    if (subObj.id in obj) {
      obj[subObj.id].value += item.value
      obj[subObj.id].lastValue += item.lastValue
    } else {
      obj[subObj.id] = {
        value: item.value,
        lastValue: item.lastValue,
        el: subObj,
      }
    }
  }

  return obj
}

function addAttribute(
  dataObj,
  items,
  itemValAttr,
  attr,
  timeChunk = 'current'
) {
  if (!(attr in dataObj)) {
    dataObj[attr] = {}
  }

  if (!items || !items?.[timeChunk]) {
    return dataObj
  }

  for (const interval of items?.[timeChunk]) {
    for (const item of interval.value) {
      const itemAttr = Object.keys(item).filter(
        (it) => it.includes('custom_attributes') && it.includes('id')
      )[0]
      const id = parseInt(item[itemAttr])

      if (id in dataObj?.[timeChunk]) {
        id in dataObj[attr]
          ? (dataObj[attr][id] += item[itemValAttr])
          : (dataObj[attr][id] = item[itemValAttr])
      }
    }
  }

  return dataObj
}

export function calcEngagementGrouped(data, sources, timeChunks, key) {
  const results = {}
  timeChunks.map((c) => {
    results[c] = {}
  })

  for (const timeChunk of timeChunks) {
    const locRes = results?.[timeChunk]
    for (const source in sources) {
      if (!['web', 'sendgrid'].includes(source)) {
        // console.log('skipping engagement grouped  ', source)
        continue
      }
      if (source in data) {
        const stream = sources[source][0]
        const str = data[source][stream]
        if (str) {
          const values = str?.[timeChunk]
          if (values) {
            for (const inter of values) {
              for (const item of inter.value) {
                const id = item[key]
                if (id !== null && 'sum_read_seconds' in item) {
                // console.log('adding sum_read_seconds from', source, stream)
                  id in locRes
                    ? (locRes[id] += item.sum_read_seconds)
                    : (locRes[id] = item.sum_read_seconds)
                }
              }
            }
          }
        }
      }
    }

    for (const itemKey in locRes) {
      // if (locRes[itemKey] !== undefined) {
        // console.log('hmmm?zc:')
      //   locRes[itemKey] > 0
      //     ? (locRes[itemKey] = Math.ceil(locRes[itemKey] / 60))
      //     : delete locRes[itemKey]
      // }
    }
  }

  // console.log({results})
  return results
}

export function processDataGrouped(
  data,
  type,
  context,
  resourceId,
  topRanks,
  timeframe
) {
  const items = []
  let results = []

  for (const id in data.current) {
    if (data.current[id] !== undefined) {
      const value = data.current[id]
      const lastValue =
        'previous' in data && id in data.previous ? data.previous[id] : 0
      const webDownloads =
        'webDownloads' in data && id in data.webDownloads
          ? data.webDownloads[id]
          : 0
      const emailViews =
        'emailViews' in data && id in data.emailViews ? data.emailViews[id] : 0
      const sendgridOpened =
        'sendgridOpened' in data && id in data.sendgridOpened
          ? data.sendgridOpened[id]
          : 0

      const diffNum = utils.percDiff(value, lastValue, false, timeframe)
      const diffStr = utils.percDiff(value, lastValue, true, timeframe)

      if ((context === 'rank' && value > -1) || value > 0) {
        items.push({
          id: id,
          name: id,
          value,
          lastValue,
          diffNum,
          diffStr,
          webDownloads,
          emailViews,
          sendgridOpened,
        })
      }
    }
  }

  utils.sortDesc(items)
  results = enrichTopListRank(items, resourceId, topRanks, context)

  return results
}

function enrichTopListRank(items, id, topRanks, context) {
  let results = items.slice()
  const halfLen = Math.round(
    Math.min(topRanks > 0 ? topRanks : 5, items.length) / 2
  )

  const activeIndex = calcChange(results, halfLen, id, 'value', 'lastValue')
  let listStart = 0
  let listEnd = 10

  if (context === 'rank') {
    listStart = Math.max(activeIndex - halfLen, 0)
    listEnd = activeIndex + halfLen
    results = results.slice(listStart, listEnd)
  } else if (context === 'start-rank') {
    results = results.slice(0, topRanks)
  } else if (context === 'infinity-rank') {
    results = results.slice(0, settings.listTopInfinityK)
  } else {
    results = results.slice(0, settings.listTopK)
  }

  results.map((item, index) => {
    item.engagement = item.value
    item.position = listStart + index + 1
  })

  return results
}

function calcChange(items, halfLen, id, value, lastValue) {
  calcRank(items, value)
  calcRank(items, lastValue)

  let activeIndex = 1

  for (const [index, item] of items.entries()) {
    item.change =
      item[lastValue + 'Rank'] < 0
        ? 0
        : item[lastValue + 'Rank'] - item[value + 'Rank']
    if (id && item.id.toString() === id.toString()) {
      activeIndex = index + 1
    }
  }

  const finalIndex =
    activeIndex - halfLen < 0
      ? halfLen
      : activeIndex + halfLen > items.length - 1
      ? items.length - halfLen
      : activeIndex

  return finalIndex
}

function calcRank(items, attr) {
  const scores = new Set(items.map((item) => item[attr]))
  const scoresOrdered = [...scores].sort((a, b) => b - a)
  for (const item of items) {
    item[attr + 'Rank'] =
      scoresOrdered.length < 2 ? -1 : scoresOrdered.indexOf(item[attr]) + 1
  }

  return items
}

export async function enrichDataGrouped(
  items,
  indexBy,
  groupBy,
  indexById,
  context = null
) {
  let results = []

  if (groupBy !== 'geo-country') {
    const infos = await getItems(groupBy, {
      [`${groupBy}_ids`]: items
        .map((it) => parseInt(it.id))
        .filter((id) => !isNaN(id)),
    })

    for (const item of items) {
      if (item.id in infos) {
        const info = infos[item.id]
        if (groupBy === 'article') {
          item.name = articleHeader(info)
          item.place = info.place
          item.tags = info.tags
        } else {
          item.name = info.name
        }

        const addons = ['account']

        for (const addon of addons) {
          item[addon] =
            addon in info && info[addon] && info[addon].name.length > 0
              ? info[addon].name
              : '-'
          item[addon + '_id'] =
            addon in info && info[addon] && info[addon].id
              ? info[addon].id
              : null
        }

        const channel =
          'channel' in info && info.channel
            ? info.channel
            : 'channel_content_type' in info &&
              info.channel_content_type &&
              'channel' in info.channel_content_type &&
              info.channel_content_type.channel
            ? info.channel_content_type.channel
            : null

        item.channel = channel && channel.name ? channel.name : '-'
        item.channel_id = channel && channel.id ? channel.id : '-'

        item.analyst = lodashGet(info.analysts, '[0]')
          ? info.analysts[0].name
          : '-'
        item.analyst_id = lodashGet(info.analysts, '[0]')
          ? info.analysts[0].id
          : null

        item.companies = lodashGet(info.companies, '[0]')
          ? info.companies
          : null

        item.email = 'email' in info ? info.email : '-'

        if (
          context === null ||
          indexBy !== 'channel' ||
          (item['channel_id'] !== undefined &&
            indexById !== null &&
            parseInt(item['channel_id']) === parseInt(indexById))
        ) {
          results.push(item)
        }
      }
      // else if (['rank', 'keep-all'].includes(context)) {
      //   item.notFound = true
      //   item.name = 'NOT FOUND'

      //   results.push(item)
      // }
    }
  } else {
    results = items
  }

  return results
}
